import React from "react";
import { _get } from "../../utils/request";
import { getPaymentFlags } from "../../utils/paymentFlags";
import { loadCatalog } from "../../i18n";
import { i18n } from "@lingui/core";
import { t } from "@lingui/macro";
import Head from "../../components/Head";
import { useUserInfo } from "../../hooks/useUserInfo";
import { useFreshStreamerData } from "../../hooks/useFreshStreamerData";
import { DonationForm } from "../../views/DonationForm";
import { VoucherForm } from "../../views/VoucherForm";
import { TokoForm } from "../../views/TokoForm";
import { ListWishlistPublic } from "../../views/WishlistPublic/ListWishlistPublic";
import { SoundboardForm } from "../../views/SoundboardForm";
import {
  StreamerPageLayout,
  useStreamerTabs,
  getPaymentFlagsFromData,
} from "../../views/StreamerPage/StreamerPageLayout";
import { MiniGamesTab } from "../../views/MiniGames/MiniGamesTab";

export async function getStaticPaths() {
  return { paths: [], fallback: true };
}

export async function getStaticProps({ params, ...ctx }) {
  const translation = await loadCatalog(ctx.locale);
  const revalidate = { revalidate: 1 };
  const paymentFlags = await getPaymentFlags();
  const { username } = params;

  // Activate i18n on server so t macro works during SSR/SSG render
  i18n.loadAndActivate({ locale: ctx.locale, messages: translation });

  try {
    const {
      json: { data: apiData },
    } = await _get(`users/${username}`);

    return {
      props: { data: { ...apiData, ...paymentFlags }, translation },
      ...revalidate,
    };
  } catch (error) {
    if (error.response) {
      const { status, json } = error.response;
      return {
        props: { data: { username }, error: { json, status }, translation },
        ...revalidate,
      };
    }
    return {
      props: {
        data: { username },
        translation,
        error: { message: "UNKNOWN_ERROR", status: 500 },
      },
      ...revalidate,
    };
  }
}

const DonatePage = ({ data: staticData = {}, error }) => {
  const { data } = useFreshStreamerData(staticData, error);
  const { data: userInfo = {} } = useUserInfo({}, () => {});
  const { username: currUsername, email: currEmail } = userInfo;
  const {
    username,
    id,
    description,
    plus,
    overlay = {},
    vote = {},
    currency,
    transaction_fee_policy: transactionFeePolicy,
    platform_fee_applied: appliedFee,
    mabar,
  } = data;

  const { isTopUp, gameVoucherEnabled, hasWishlist, soundboard, hasStore, miniGamesEnabled } = useStreamerTabs(data);
  const paymentFlags = getPaymentFlagsFromData(data);

  const head = (
    <Head title={t`Dukung ${username}`}>
      <meta name="description" content={description || t`Dukung ${username} via Saweria. Kirim dukungan langsung ke kreator favorit kamu.`} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://saweria.co/${username}`} />
      <meta property="og:title" content={t`Dukung ${username} via Saweria`} />
      <meta property="og:description" content={description || t`Dukung ${username} via Saweria. Kirim dukungan langsung ke kreator favorit kamu.`} />
      <meta property="og:image" content="https://saweria.co/twitter_card.png" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@saweriaco" />
      <meta name="twitter:title" content={t`Dukung ${username} via Saweria`} />
      <meta name="twitter:description" content={description || t`Dukung ${username} via Saweria. Kirim dukungan langsung ke kreator favorit kamu.`} />
      <meta name="twitter:image" content="https://saweria.co/twitter_card.png" />
    </Head>
  );

  // Build tab panels in order matching tabRoutes
  const panels = [];

  if (!isTopUp) {
    // Dukungan tab
    panels.push(
      <DonationForm
        key="donation"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        appliedFee={appliedFee}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
        mabar={mabar}
        challenge={data.challenge}
      />
    );
  }

  if (isTopUp || gameVoucherEnabled) {
    // Top Up tab
    panels.push(
      <VoucherForm
        key="voucher"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  if (!isTopUp && hasStore) {
      panels.push(
        <TokoForm
          key="toko"
          plus={plus}
          overlay={overlay}
          vote={vote}
          streamerId={id}
          {...paymentFlags}
          currEmail={currEmail}
          currUsername={currUsername}
          streamerUsername={username}
          currency={currency}
          transactionFeePolicy={transactionFeePolicy}
        />
      );
    }

  if (!isTopUp && miniGamesEnabled) {
    panels.push(
      <MiniGamesTab
        key="mini-games"
        streamerUsername={data.username}
        streamerId={data.id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  if (!isTopUp && hasWishlist) {
    panels.push(
      <ListWishlistPublic
        key="wishlist"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        streamerUsername={username}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  if (!isTopUp && soundboard) {
    panels.push(
      <SoundboardForm
        key="soundboard"
        plus={plus}
        overlay={overlay}
        vote={vote}
        streamerId={id}
        {...paymentFlags}
        currEmail={currEmail}
        currUsername={currUsername}
        currency={currency}
        transactionFeePolicy={transactionFeePolicy}
      />
    );
  }

  return (
    <StreamerPageLayout data={data} error={error} head={head}>
      {panels}
    </StreamerPageLayout>
  );
};

export default DonatePage;
